import React, { useState, useMemo } from 'react';

/**
 * Renderiza un mensaje de log de Monolog detectando JSONs embebidos.
 *
 * Monolog escribe context/extra al final del mensaje con la forma:
 *   "texto del log {context-json} [extra-json]"
 *
 * Este componente:
 *   - Escanea la string buscando substrings balanceadas que sean JSON válido.
 *   - Renderiza cada JSON colapsado por default ({…} o […]) con un caret para expandir.
 *   - Deja los objetos/arrays vacíos como texto plano para no agregar ruido.
 */
export default function MonologMessage({ message }) {
  if (!message) {
    return null;
  }
  var parts = splitJsonInMessage(message);
  return (
    <span>
      {parts.map(function (part, i) {
        if (part.type === 'json') {
          return <CollapsibleJson key={i} value={part.value} />;
        }
        return <span key={i}>{part.value}</span>;
      })}
    </span>
  );
}

function CollapsibleJson({ value }) {
  var initial = useState(false);
  var expanded = initial[0];
  var setExpanded = initial[1];
  var isArray = Array.isArray(value);
  var placeholder = isArray ? '[…]' : '{…}';
  // Solo serializar cuando el bloque está expandido — context/extra de Monolog
  // pueden ser pesados y la mayoría de las filas se renderizan colapsadas.
  var formatted = useMemo(function () {
    if (!expanded) {
      return '';
    }
    try {
      return JSON.stringify(value, null, 2);
    } catch (e) {
      return String(value);
    }
  }, [expanded, value]);
  return (
    <span className="monolog-json">
      <button
        type="button"
        className="monolog-json-toggle"
        onClick={function () { setExpanded(!expanded); }}
        title={expanded ? 'Colapsar' : 'Expandir'}
      >
        <i className={'fa fa-caret-' + (expanded ? 'down' : 'right')}></i>
        {!expanded && <span className="monolog-json-placeholder">{placeholder}</span>}
      </button>
      {expanded && <pre className="monolog-json-content">{formatted}</pre>}
    </span>
  );
}

/**
 * Recorre la string y separa partes de texto plano de los JSONs detectados.
 * Devuelve un array de { type: 'text'|'json', value }.
 */
function splitJsonInMessage(text) {
  var parts = [];
  var i = 0;
  var len = text.length;
  var bufferStart = 0;

  while (i < len) {
    var ch = text[i];
    if (ch === '{' || ch === '[') {
      var endIdx = findBalancedEnd(text, i);
      if (endIdx > i) {
        var candidate = text.substring(i, endIdx + 1);
        // Saltear vacíos triviales — Monolog los escribe cuando context/extra están vacíos.
        if (candidate === '{}' || candidate === '[]') {
          i = endIdx + 1;
          continue;
        }
        try {
          var parsed = JSON.parse(candidate);
          if (parsed && typeof parsed === 'object') {
            if (i > bufferStart) {
              parts.push({ type: 'text', value: text.substring(bufferStart, i) });
            }
            parts.push({ type: 'json', value: parsed });
            bufferStart = endIdx + 1;
            i = endIdx + 1;
            continue;
          }
        } catch (e) {
          // No es JSON válido — sigue siendo texto plano.
        }
      }
    }
    i++;
  }
  if (bufferStart < len) {
    parts.push({ type: 'text', value: text.substring(bufferStart) });
  }
  return parts;
}

/**
 * A partir de un `{` o `[` en `start`, busca el cierre balanceado.
 * Respeta strings con escapes. Devuelve el índice del cierre, o -1 si no balanceó.
 */
function findBalancedEnd(text, start) {
  var depth = 0;
  var inString = false;
  var escape = false;
  for (var i = start; i < text.length; i++) {
    var ch = text[i];
    if (escape) {
      escape = false;
      continue;
    }
    if (inString) {
      if (ch === '\\') {
        escape = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '{' || ch === '[') {
      depth++;
    } else if (ch === '}' || ch === ']') {
      depth--;
      if (depth === 0) {
        return i;
      }
      if (depth < 0) {
        return -1;
      }
    }
  }
  return -1;
}
